import React from 'react';
import { FaDownload, FaFileAlt, FaTimes, FaEye } from 'react-icons/fa';
import Button from '../../../../components/common/Button';
import { API_BASE_URL } from '../../../../config';

const FilesModal = ({ show, onHide, files = [], title = "Files" }) => {
  if (!show) return null;

  // Helper function to extract file extension
  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    const extension = fileName.split('.').pop();
    return extension ? extension.toUpperCase() : '';
  };

  // Helper function to get file icon based on extension
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'xls': case 'xlsx': return '📊';
      case 'ppt': case 'pptx': return '📊';
      case 'zip': case 'rar': case '7z': return '🗜️';
      case 'txt': return '📄';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': case 'webp': return '🖼️';
      default: return '📎';
    }
  };

  // Helper function to check if file is an image
  const isImageFile = (fileName) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const ext = fileName?.split('.').pop()?.toLowerCase();
    return imageExtensions.includes(ext);
  };

  const handleDownload = (file, forceDownload = false) => {
    try {
      // Check if this is a new standardized attachment (has attachment_id)
      if (file.attachment_id) {
        // Use new standardized download endpoint through API (following helpdesk pattern)
        const downloadUrl = `${API_BASE_URL}/api/attachments/requisitions/${file.file_path}${forceDownload ? '?download=1' : ''}`;
        if (forceDownload) {
          // Force download
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = file.file_name || file.name || 'download';
          link.click();
        } else {
          // Open in new window for viewing
          window.open(downloadUrl, '_blank');
        }
      } else {
        // Legacy files - use backend for downloads, direct access for viewing
        if (forceDownload) {
          // Use backend controller for forced download
          const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(file.file_path || file.url)}`;
          window.open(downloadUrl, '_blank');
        } else {
          // Use direct file access for viewing
          const viewUrl = `${API_BASE_URL.replace('/portal', '')}/${file.file_path || file.url}`;
          window.open(viewUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onHide}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {files && files.length > 0 ? (
            (() => {
              // Separate images from other files for better display
              const images = files.filter(file => 
                isImageFile(file.file_name || file.name)
              );
              const documents = files.filter(file => 
                !isImageFile(file.file_name || file.name)
              );

              return (
                <div className="space-y-6">
                  {/* Images Section */}
                  {images.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <FaEye className="w-5 h-5 mr-2" />
                        Images ({images.length})
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {images.map((image, index) => {
                          const isNewFormat = image.attachment_id;
                          const imageUrl = isNewFormat 
                            ? `${API_BASE_URL}/api/attachments/requisitions/${image.file_path}`
                            : `${API_BASE_URL}app/files/storage?file=${image.file_path}`;
                          
                          return (
                            <div key={index} className="group relative bg-gray-50 rounded-lg border overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleDownload(image, false)}>
                              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                <img 
                                  src={imageUrl} 
                                  alt={image.file_name || image.name || `Image ${index + 1}`}
                                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full text-4xl">🖼️</div>';
                                  }}
                                />
                              </div>
                              <div className="p-2 bg-white border-t">
                                <p className="text-xs font-medium text-gray-900 truncate" title={image.file_name || image.name}>
                                  {image.file_name || image.name || `Image ${index + 1}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {image.file_size ? `${Math.round(image.file_size / 1024)} KB` : 'Image'}
                                </p>
                              </div>
                              {/* Overlay with actions */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                                <div className="flex space-x-2 pointer-events-auto">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(image, false);
                                    }}
                                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                    title="View full size"
                                  >
                                    <FaEye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(image, true);
                                    }}
                                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                                    title="Download"
                                  >
                                    <FaDownload className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Documents Section */}
                  {documents.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <FaFileAlt className="w-5 h-5 mr-2" />
                        Documents ({documents.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {documents.map((file, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors group">
                            <div className="text-2xl flex-shrink-0">
                              {getFileIcon(file.file_name || file.name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={file.file_name || file.name}>
                                {file.file_name || file.name || `File ${index + 1}`}
                              </p>
                              <p className="text-xs text-gray-500">
                                {file.file_size ? `${Math.round(file.file_size / 1024)} KB` : 'Unknown size'}
                                {file.file_type && ` • ${getFileExtension(file.file_name || file.name)}`}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDownload(file, false)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="View"
                              >
                                <FaEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownload(file, true)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Download"
                              >
                                <FaDownload className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FaFileAlt size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No files available</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilesModal;
